import { FC, useCallback, useState, useEffect } from 'react'
import moment, { Moment } from 'moment-timezone'
import PerDatePicker from '../../../components/custom_components/DatePicker'
import { KudosFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { useTranslation } from 'react-i18next'

interface MonthYearFilterProps {
  filters: KudosFilterInput
  onFiltersChange: (filters: KudosFilterInput) => void
  filterType: 'from' | 'to'
}

export const MonthYearFilter: FC<MonthYearFilterProps> = ({ filters, onFiltersChange, filterType }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { utcDateStringToMoment, momentToUtcDateString } = useDateTime({ locale: user.locale, timezone: user.timezone })

  const [selectedDate, setSelectedDate] = useState<Moment | null>(null)

  // Initialize selectedDate from filters
  useEffect(() => {
    const dateToCheck = filterType === 'from' ? filters.startDate : filters.endDate
    if (dateToCheck) {
      // Convert UTC date back to user's timezone for display
      const dateMoment = moment(dateToCheck).tz(user.timezone)
      setSelectedDate(dateMoment)
    } else {
      setSelectedDate(null)
    }
  }, [filterType, filters.startDate, filters.endDate, user.timezone])

  const handleDateChange = useCallback(
    (date: string | undefined) => {
      if (date) {
        const newDate: Moment | undefined = utcDateStringToMoment(date)
        if (newDate) {
          setSelectedDate(newDate)

          if (filterType === 'from') {
            // Calculate start of month in user's timezone, then convert to UTC
            const startOfMonth = newDate.clone().startOf('month').utc().toISOString()
            onFiltersChange({
              ...filters,
              startDate: startOfMonth,
            })
          } else {
            // Calculate end of month in user's timezone, then convert to UTC
            const endOfMonth = newDate.clone().endOf('month').utc().toISOString()
            onFiltersChange({
              ...filters,
              endDate: endOfMonth,
            })
          }
        }
      } else {
        setSelectedDate(null)
        if (filterType === 'from') {
          onFiltersChange({
            ...filters,
            startDate: undefined,
          })
        } else {
          onFiltersChange({
            ...filters,
            endDate: undefined,
          })
        }
      }
    },
    [filters, onFiltersChange, utcDateStringToMoment, filterType],
  )

  return (
    <PerDatePicker
      placeholder={t('kudos.management.filters.date')}
      allowClear={true}
      showToday={false}
      inputDate={selectedDate ? momentToUtcDateString(selectedDate) || '' : ''}
      format={'MM/YYYY'}
      dateFormat="MY"
      timeZoneAndLocale={{ locale: user.locale, timezone: user.timezone }}
      size="small"
      width={'100%'}
      disabledDate={(current: Moment | null) => {
        // Disable future dates
        return !!current && current.isAfter(moment(), 'month')
      }}
      onDateChanged={handleDateChange}
    />
  )
}
